import { useCallback, useEffect, useRef, useState, type PointerEvent, type RefObject } from 'react';
import {
  AT_REST,
  DOUBLE_TAP_ZOOM,
  MAX_ZOOM,
  MIN_ZOOM,
  SWIPE_SLOP_PX,
  clampPan,
  clampZoom,
  containRect,
  panBounds,
  zoomAbout,
  type Point,
  type Size,
  type ZoomView,
} from '@/shared/image/viewerMath';

/** Two taps are one double tap when the second lands within this long and this far of the first. */
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 30;

/** How much a wheel notch zooms: a pixel of wheel is this much of an exponent. */
const WHEEL_RATE = 0.002;
/** A wheel that counts in lines rather than pixels, and what a line is worth. */
const DOM_DELTA_LINE = 1;
const WHEEL_LINE_PX = 16;

/** A one finger drag of a picture that is not zoomed. The owner's, not the hook's. */
export interface PinchDrag {
  dx: number;
  dy: number;
}

export interface PinchDragEnd extends PinchDrag {
  /** How fast the finger was going when it let go, in pixels a millisecond. */
  velocity: Point;
  /** How many moves the drag was. A flick is a run of them, never a single jump. */
  moves: number;
}

export interface PinchZoomOptions {
  /** The range of the zoom. The whole picture to four times it unless told otherwise. */
  min?: number;
  max?: number;
  /** What a double tap zooms to; a second one returns to the whole picture. */
  doubleTapZoom?: number;
  /**
   * The picture's own size. It is drawn to fit the stage whole, as
   * `object-fit: contain` draws it, and a drag stops at its edges. Without it
   * the drag stops at the edges of the stage itself.
   */
  natural?: Size | null;
  /** Nothing is taken up while this is set, as while the viewer is closing. */
  disabled?: boolean;
  /**
   * One finger on a picture that is not zoomed is not a drag of the picture:
   * it is the owner's swipe between pictures or down to close, and the owner
   * hears every move and the moment it is let go. A drag that a second finger
   * turns into a pinch ends as no drag at all.
   */
  onDragMove?: (drag: PinchDrag) => void;
  onDragEnd?: (drag: PinchDragEnd) => void;
}

export interface PinchZoomHandlers {
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
}

export interface PinchZoom {
  /** How the picture is being looked at: draw it with `zoomTransform(view)`. */
  view: ZoomView;
  /** Closer than the whole picture. */
  zoomed: boolean;
  /** Fingers are on it: what they move must follow them at once, with no easing. */
  following: boolean;
  /** For the stage the picture is on. */
  handlers: PinchZoomHandlers;
  /** Zoom to `zoom`, about a point on the screen, or about the middle of the stage. */
  zoomTo: (zoom: number, at?: Point) => void;
  /** The whole picture, in the middle. */
  reset: () => void;
}

interface Sample extends Point {
  t: number;
}

/** What the fingers on the stage are doing. */
type Gesture =
  /** One finger, the picture not zoomed: the owner's swipe. */
  | { kind: 'drag'; start: Point; moves: number; trail: Sample[] }
  /** One finger, the picture zoomed: it moves with the finger. */
  | { kind: 'pan'; start: Point; moved: boolean }
  /** Two fingers: the picture zooms about the point between them, and goes where they go. */
  | { kind: 'pinch'; distance: number; zoom: number; mid: Point }
  /** What is left of a pinch once a finger has lifted: nothing more until the other does too. */
  | { kind: 'spent' };

const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

/** How fast the finger was going at the end of its trail. */
function speed(trail: readonly Sample[]): Point {
  const [from, to] = trail;
  if (from === undefined || to === undefined || to.t <= from.t) return { x: 0, y: 0 };
  return { x: (to.x - from.x) / (to.t - from.t), y: (to.y - from.y) / (to.t - from.t) };
}

/**
 * Pinch to zoom, double tap to zoom, drag to move once zoomed, and the wheel
 * on a desk, for a picture on a stage. The request of 5 October 2026 (third),
 * section 6.
 *
 * The hook keeps the fingers and the view; the owner draws the view and gives
 * the stage the handlers. A pinch zooms about the point between the fingers
 * and a double tap about where it landed, so the part being looked at stays
 * under the fingers. The zoom runs from the whole picture to four times it,
 * and a drag stops where the picture's edge meets the stage's; back at the
 * whole picture the drag is forgotten, so the next picture starts in the
 * middle. The stage should have `touch-action: none`, or the browser takes the
 * pinch for the page.
 *
 * One finger on a picture that is not zoomed is handed to the owner as a drag
 * (`onDragMove`, `onDragEnd`), for the swipe between pictures and down to
 * close, which are the owner's to judge.
 */
export function usePinchZoom(stage: RefObject<HTMLElement | null>, options: PinchZoomOptions = {}): PinchZoom {
  const [view, setView] = useState<ZoomView>(AT_REST);
  const [following, setFollowing] = useState(false);
  // The view as of the last event, which the next event reads before React has drawn it.
  const current = useRef<ZoomView>(AT_REST);
  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef<Gesture | null>(null);
  const lastTap = useRef<Sample | null>(null);
  // Read at the time of each event, never from the render the handlers were made in.
  const settings = useRef(options);
  settings.current = options;

  const range = useCallback(
    () => ({ min: settings.current.min ?? MIN_ZOOM, max: settings.current.max ?? MAX_ZOOM }),
    [],
  );

  /** The stage, where it is on the screen. Nothing, in a test with no layout. */
  const room = useCallback((): { left: number; top: number; width: number; height: number } => {
    const node = stage.current;
    if (node === null) return { left: 0, top: 0, width: 0, height: 0 };
    const box = node.getBoundingClientRect();
    return { left: box.left, top: box.top, width: box.width, height: box.height };
  }, [stage]);

  /** A point on the screen, measured from the middle of the stage. */
  const fromCentre = useCallback(
    (point: Point): Point => {
      const box = room();
      return { x: point.x - (box.left + box.width / 2), y: point.y - (box.top + box.height / 2) };
    },
    [room],
  );

  /** Takes a view, held inside the zoom's range and the picture's edges, and draws it. */
  const commit = useCallback(
    (next: ZoomView) => {
      const { min, max } = range();
      const zoom = clampZoom(next.zoom, min, max);
      let settled: ZoomView;
      if (zoom <= min) {
        settled = { zoom: min, x: 0, y: 0 };
      } else {
        const box = room();
        const natural = settings.current.natural ?? null;
        const content = natural === null ? box : containRect(natural, box);
        const bounds = panBounds(box, content, zoom);
        settled = { zoom, ...clampPan({ x: next.x, y: next.y }, bounds) };
      }
      const was = current.current;
      if (was.zoom === settled.zoom && was.x === settled.x && was.y === settled.y) return;
      current.current = settled;
      setView(settled);
    },
    [room, range],
  );

  const zoomTo = useCallback(
    (zoom: number, at?: Point) => {
      const { min, max } = range();
      const focal = at === undefined ? { x: 0, y: 0 } : fromCentre(at);
      commit(zoomAbout(current.current, clampZoom(zoom, min, max), focal));
    },
    [commit, fromCentre, range],
  );

  const reset = useCallback(() => {
    lastTap.current = null;
    commit({ zoom: range().min, x: 0, y: 0 });
  }, [commit, range]);

  /** A tap, and whether it was the second of a double tap. */
  function tap(at: Point, time: number) {
    const last = lastTap.current;
    if (last !== null && time - last.t <= DOUBLE_TAP_MS && distance(at, last) <= DOUBLE_TAP_PX) {
      lastTap.current = null;
      const now = current.current;
      const { min, max } = range();
      if (now.zoom > min) commit({ zoom: min, x: 0, y: 0 });
      else commit(zoomAbout(now, clampZoom(settings.current.doubleTapZoom ?? DOUBLE_TAP_ZOOM, min, max), fromCentre(at)));
      return;
    }
    lastTap.current = { ...at, t: time };
  }

  function down(event: PointerEvent<HTMLElement>) {
    if (settings.current.disabled === true) return;
    // A mouse's other buttons are the browser's; a button or a link on the stage is its own.
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if ((event.target as Element).closest('button, a, input, textarea') !== null) return;
    // A third finger changes nothing.
    if (pointers.current.size >= 2) return;

    event.currentTarget.setPointerCapture?.(event.pointerId);
    const at = { x: event.clientX, y: event.clientY };
    pointers.current.set(event.pointerId, at);
    setFollowing(true);

    if (pointers.current.size === 1) {
      gesture.current =
        current.current.zoom > range().min
          ? { kind: 'pan', start: at, moved: false }
          : { kind: 'drag', start: at, moves: 0, trail: [{ ...at, t: event.timeStamp }] };
      return;
    }

    // A second finger: whatever the first was doing, it is a pinch now.
    const was = gesture.current;
    if (was?.kind === 'drag' && was.moves > 0) {
      settings.current.onDragEnd?.({ dx: 0, dy: 0, velocity: { x: 0, y: 0 }, moves: 0 });
    }
    const [a, b] = [...pointers.current.values()];
    if (a === undefined || b === undefined) return;
    gesture.current = { kind: 'pinch', distance: distance(a, b), zoom: current.current.zoom, mid: midpoint(a, b) };
    lastTap.current = null;
  }

  function move(event: PointerEvent<HTMLElement>) {
    const before = pointers.current.get(event.pointerId);
    if (before === undefined) return;
    const now = { x: event.clientX, y: event.clientY };
    pointers.current.set(event.pointerId, now);
    const doing = gesture.current;
    if (doing === null) return;

    switch (doing.kind) {
      case 'pinch': {
        const [a, b] = [...pointers.current.values()];
        if (a === undefined || b === undefined || doing.distance <= 0) return;
        const mid = midpoint(a, b);
        const { min, max } = range();
        // Zoomed about the point between the fingers, then carried with it as the fingers move together.
        const zoomed = zoomAbout(
          current.current,
          clampZoom(doing.zoom * (distance(a, b) / doing.distance), min, max),
          fromCentre(mid),
        );
        commit({ zoom: zoomed.zoom, x: zoomed.x + mid.x - doing.mid.x, y: zoomed.y + mid.y - doing.mid.y });
        doing.mid = mid;
        return;
      }
      case 'pan': {
        if (!doing.moved && distance(now, doing.start) > SWIPE_SLOP_PX) doing.moved = true;
        const at = current.current;
        commit({ zoom: at.zoom, x: at.x + now.x - before.x, y: at.y + now.y - before.y });
        return;
      }
      case 'drag': {
        doing.moves += 1;
        doing.trail = [...doing.trail.slice(-1), { ...now, t: event.timeStamp }];
        settings.current.onDragMove?.({ dx: now.x - doing.start.x, dy: now.y - doing.start.y });
        return;
      }
      case 'spent':
        return;
    }
  }

  /** A finger lifted, or taken away by the browser. */
  function finish(event: PointerEvent<HTMLElement>, cancelled: boolean) {
    // Where the finger last was: the moment of letting go carries no better place.
    const at = pointers.current.get(event.pointerId);
    if (at === undefined) return;
    pointers.current.delete(event.pointerId);
    const doing = gesture.current;
    const left = pointers.current.size;
    if (left === 0) {
      gesture.current = null;
      setFollowing(false);
    }
    if (doing === null) return;

    switch (doing.kind) {
      case 'pinch': {
        // One finger stays down. Zoomed, it carries on moving the picture; otherwise it does nothing more.
        if (left !== 1) return;
        const [other] = [...pointers.current.values()];
        gesture.current =
          current.current.zoom > range().min && other !== undefined
            ? { kind: 'pan', start: other, moved: true }
            : { kind: 'spent' };
        return;
      }
      case 'pan':
        if (!cancelled && !doing.moved) tap(at, event.timeStamp);
        return;
      case 'drag': {
        const dx = at.x - doing.start.x;
        const dy = at.y - doing.start.y;
        if (doing.moves > 0) {
          settings.current.onDragEnd?.(
            cancelled
              ? { dx: 0, dy: 0, velocity: { x: 0, y: 0 }, moves: 0 }
              : { dx, dy, velocity: speed(doing.trail), moves: doing.moves },
          );
        }
        if (!cancelled && Math.hypot(dx, dy) <= SWIPE_SLOP_PX) tap(at, event.timeStamp);
        return;
      }
      case 'spent':
        return;
    }
  }

  /*
   * The wheel, on a desk: zooms about the pointer. Listened to by hand because
   * React's wheel listener is passive, and the page must not scroll or zoom
   * under a picture being zoomed.
   */
  useEffect(() => {
    const node = stage.current;
    if (node === null) return undefined;
    const onWheel = (event: WheelEvent) => {
      if (settings.current.disabled === true) return;
      event.preventDefault();
      const pixels = event.deltaMode === DOM_DELTA_LINE ? event.deltaY * WHEEL_LINE_PX : event.deltaY;
      const now = current.current;
      const { min, max } = range();
      commit(
        zoomAbout(
          now,
          clampZoom(now.zoom * Math.exp(-pixels * WHEEL_RATE), min, max),
          fromCentre({ x: event.clientX, y: event.clientY }),
        ),
      );
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [stage, commit, fromCentre, range]);

  return {
    view,
    zoomed: view.zoom > (options.min ?? MIN_ZOOM),
    following,
    handlers: {
      onPointerDown: down,
      onPointerMove: move,
      onPointerUp: (event) => finish(event, false),
      onPointerCancel: (event) => finish(event, true),
    },
    zoomTo,
    reset,
  };
}
